"use client";

import { useEffect, useRef, useCallback, useMemo, useState } from "react";
import {
  quotesCacheService,
  type CachedQuote,
} from "@/services/quotes-cache-service";
import {
  greeksCacheService,
  type CachedGreeks,
} from "@/services/greeks-cache-service.client";
import { useMarketDataStreams } from "./use-market-data-streams";
import { symbolMappingService } from "@/services/symbol-mapping-service";

export interface MarketDataSymbol {
  symbol: string; // Position symbol
  instrumentType: "stock" | "option";
  streamerSymbol?: string; // Pre-computed streamer symbol for options
  underlyingSymbol?: string; // Underlying symbol for options
}

export interface MarketDataResult {
  quotes: Map<string, CachedQuote>;
  greeks: Map<string, CachedGreeks>;
  isConnected: boolean;
  error: string | null;
  subscribe: (symbols: MarketDataSymbol[]) => void;
  unsubscribe: () => void;
  stats: {
    quotes: {
      total: number;
      highConfidence: number;
      stale: number;
    };
    greeks: {
      total: number;
      highConfidence: number;
      stale: number;
    };
  };
}

export function useMarketData(): MarketDataResult {
  const [quotes, setQuotes] = useState<Map<string, CachedQuote>>(new Map());
  const [greeks, setGreeks] = useState<Map<string, CachedGreeks>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [requestedSymbols, setRequestedSymbols] = useState<string[]>([]);

  const { status, errors: streamErrors } =
    useMarketDataStreams(requestedSymbols, { includeGreeks: true });
  const updateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const isConnected = useMemo(() => {
    if (requestedSymbols.length === 0) {
      return false;
    }
    const values = Object.values(status ?? {});
    if (values.length === 0) {
      return false;
    }
    if (values.some((value) => value === "error")) {
      return false;
    }
    return values.some((value) => value === "connected");
  }, [requestedSymbols.length, status]);

  // (moved effects below scheduleStateUpdate to avoid TDZ)

  // Update from cache periodically
  const updateFromCache = useCallback(() => {
    // Update quotes from cache
    const cachedQuotes = quotesCacheService.getAllQuotes();
    const newQuotes = new Map<string, CachedQuote>();

    cachedQuotes.forEach((quote, symbol) => {
      newQuotes.set(symbol, quote);
    });

    setQuotes(newQuotes);

    // Update Greeks from cache
    const cachedGreeks = greeksCacheService.getAllGreeks();
    const newGreeks = new Map<string, CachedGreeks>();

    cachedGreeks.forEach((greek, symbol) => {
      newGreeks.set(symbol, greek);
    });

    // Debug logging for HOOD options
    if (process.env.NODE_ENV === "development") {
      const hoodGreeks = Array.from(cachedGreeks.entries()).filter(([symbol]) =>
        symbol.includes("HOOD"),
      );
      if (hoodGreeks.length > 0) {
        // HOOD Greeks in cache
      }
    }

    setGreeks(newGreeks);
  }, []);

  // Set up periodic cache updates (fallback if no events)
  useEffect(() => {
    let cancelled = false;
    // Initial update
    updateFromCache();

    quotesCacheService
      .hydrateFromPersistedCache()
      .then(() => {
        if (!cancelled) {
          updateFromCache();
        }
      })
      .catch(() => undefined);

    // Update every 1 second as a fallback cadence
    updateIntervalRef.current = setInterval(updateFromCache, 1000);

    return () => {
      cancelled = true;
      if (updateIntervalRef.current) {
        clearInterval(updateIntervalRef.current);
        updateIntervalRef.current = null;
      }
    };
  }, [updateFromCache]);

  useEffect(() => {
    const activeErrors = Object.values(streamErrors ?? {}).filter(
      (value): value is string => typeof value === "string" && value.length > 0,
    );
    setError(activeErrors.length > 0 ? activeErrors[0] : null);
  }, [streamErrors]);

  const unsubscribe = useCallback(() => {
    setRequestedSymbols([]);
    setError(null);
  }, []);

  // Subscribe to symbols with intelligent type selection
  const subscribe = useCallback(
    (symbols: MarketDataSymbol[]) => {
      if (!symbols.length) {
        unsubscribe();
        return;
      }

      if (!isConnected) {
        setError("Market data stream not connected");
      } else {
        setError(null);
      }

      setError(null);
      const next = new Set<string>();

      symbols.forEach((symbolData) => {
        const rawSymbol = symbolData.symbol?.toString() ?? "";
        const trimmedSymbol = rawSymbol.trim();
        if (!trimmedSymbol) {
          return;
        }

        const normalizedSymbol = trimmedSymbol.toUpperCase();
        next.add(normalizedSymbol);

        const pushUnderlying = (value: unknown) => {
          const raw = value?.toString() ?? "";
          const trimmed = raw.trim();
          if (trimmed) {
            next.add(trimmed.toUpperCase());
          }
        };

        if (symbolData.instrumentType === "option") {
          const providedStreamer = symbolData.streamerSymbol?.toString().trim();
          const inferredStreamer =
            providedStreamer && providedStreamer.length > 0
              ? providedStreamer
              : symbolMappingService.getStreamerSymbol(trimmedSymbol);

          if (inferredStreamer) {
            next.add(inferredStreamer.trim().toUpperCase());
          }

          pushUnderlying(symbolData.underlyingSymbol);
        } else {
          pushUnderlying(symbolData.underlyingSymbol);
        }
      });

      setRequestedSymbols(Array.from(next));
    },
    [isConnected, unsubscribe],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  // Calculate statistics
  const stats = {
    quotes: {
      total: quotes.size,
      highConfidence: Array.from(quotes.values()).filter(
        (q) => q.confidence > 0.8,
      ).length,
      stale: Array.from(quotes.values()).filter((q) => {
        const ageMinutes = (Date.now() - q.timestamp) / 60000;
        return ageMinutes > 2;
      }).length,
    },
    greeks: {
      total: greeks.size,
      highConfidence: Array.from(greeks.values()).filter(
        (g) => g.confidence > 0.7,
      ).length,
      stale: Array.from(greeks.values()).filter((g) => {
        const ageMinutes = (Date.now() - g.timestamp) / 60000;
        return ageMinutes > 5;
      }).length,
    },
  };

  return {
    quotes,
    greeks,
    isConnected,
    error,
    subscribe,
    unsubscribe,
    stats,
  };
}
